import { useState } from "react";
import { Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAssignSims, useSimCards } from "@/hooks/useSims";
import {
  ICCID_SUFFIX_MIN_DIGITS,
  PROVIDER_LABELS,
  findSimsByIccid,
  type SimCard,
} from "@/lib/sims";

/**
 * Link a SIM to a device by typing or scanning its ICCID (or its last digits).
 * Enter submits; SIMs assigned elsewhere are moved to this device.
 */
export function LinkSimForm({
  instanceId,
  deviceSims,
}: {
  instanceId: string;
  deviceSims: SimCard[];
}) {
  const { data: allSims, isLoading } = useSimCards();
  const assign = useAssignSims();
  const [iccid, setIccid] = useState("");
  const [slot, setSlot] = useState("");

  const digits = iccid.replace(/\D/g, "");
  const matches = digits ? findSimsByIccid(allSims, iccid) : [];
  const match = matches.length === 1 ? matches[0] : null;
  const alreadyHere = match?.equipmentInstanceID === instanceId;

  const takenSlots = deviceSims
    .filter((s) => s.slot !== null && s.id !== match?.id)
    .map((s) => s.slot as number);
  const slotNumber = slot.trim() ? Number(slot) : null;
  const slotInvalid =
    slotNumber !== null && (!Number.isInteger(slotNumber) || slotNumber < 1);
  const slotTaken = slotNumber !== null && takenSlots.includes(slotNumber);

  let hint: { text: string; error?: boolean } | null = null;
  if (!digits) hint = null;
  else if (isLoading) hint = { text: "Loading SIMs…" };
  else if (matches.length === 0)
    hint =
      digits.length < ICCID_SUFFIX_MIN_DIGITS
        ? {
            text: `Enter the full ICCID or at least its last ${ICCID_SUFFIX_MIN_DIGITS} digits.`,
          }
        : { text: "No SIM with this ICCID.", error: true };
  else if (matches.length > 1)
    hint = {
      text: `${matches.length} SIMs end with ${digits}. Enter more digits.`,
      error: true,
    };
  else if (alreadyHere)
    hint = { text: `${match!.iccid} is already in this device.` };
  else
    hint = {
      text: `${match!.iccid} · ${PROVIDER_LABELS[match!.provider]}${
        match!.deviceName
          ? ` · will be moved from ${match!.deviceName}${match!.slot ? ` (slot ${match!.slot})` : ""}`
          : ""
      }`,
    };
  if (slotInvalid)
    hint = { text: "Slot must be a positive whole number.", error: true };
  else if (slotTaken)
    hint = { text: `Slot ${slotNumber} is already taken.`, error: true };

  const canSubmit =
    !!match &&
    !(alreadyHere && slotNumber === match.slot) &&
    !slotInvalid &&
    !slotTaken &&
    !assign.isPending;

  const submit = () => {
    if (!canSubmit || !match) return;
    assign.mutate(
      { simIds: [match.id], equipmentInstanceId: instanceId, slot: slotNumber },
      {
        onSuccess: () => {
          setIccid("");
          setSlot("");
        },
      },
    );
  };

  return (
    <form
      className="space-y-1"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      data-testid="link-sim-form"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={iccid}
          onChange={(e) => setIccid(e.target.value)}
          placeholder="Link SIM: enter or scan ICCID (or last digits)"
          className="min-w-64 flex-1 font-mono text-sm"
          data-testid="link-sim-iccid"
          autoComplete="off"
        />
        <Input
          value={slot}
          onChange={(e) => setSlot(e.target.value)}
          placeholder="Slot"
          inputMode="numeric"
          className="w-20"
          data-testid="link-sim-slot"
        />
        <Button
          type="submit"
          size="sm"
          disabled={!canSubmit}
          data-testid="link-sim-submit"
        >
          <Link2 className="mr-1.5 h-4 w-4" />
          {assign.isPending ? "Linking…" : "Link SIM"}
        </Button>
      </div>
      {hint && (
        <p
          className={`text-xs ${hint.error ? "text-destructive" : "text-muted-foreground"}`}
          data-testid="link-sim-hint"
        >
          {hint.text}
        </p>
      )}
    </form>
  );
}
